<template>
  <Downtime v-if="isDowntimeEnabled" />
  <div v-else id="base-0" class="z-0 absolute w-screen h-screen">
    <component :is="layout" :key="layoutName">
      <RouterView :key="$route.path" />
    </component>
  </div>
  <div
    id="overlay-layer-1"
    class="z-1 absolute"
  />
  <div
    id="modal-layer-2"
    class="z-5 absolute"
  />
  <div
    id="modal-overlay-layer-3"
    class="z-10 absolute"
  />
  <div
    id="third-party-layer-4"
    class="z-20 absolute"
  />
  <div
    id="layer-1"
    class="z-1 absolute"
  />
  <div
    id="layer-2"
    class="z-5 absolute"
  />
  <div
    id="layer-3"
    class="z-10 absolute"
  />
  <div
    id="layer-4"
    class="z-20 absolute"
  />
  <div
    id="layer-5"
    class="z-50 absolute"
  />
</template>

<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  getCurrentInstance,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import get from 'lodash/get';
import Layouts from '~/layouts/index';
import watchForLibraryLimits from './components/MyLibraryV2/helpers/watchForLibraryLimits';
import { isServer } from './utils/EnvUtils';
import useUserDerivedDetails from '~/composables/useUserDerivedDetails';
import Constants from '~/config/Constants';
import HotjarService, { HotjarEvents } from '~/services/HotjarService';

const Downtime = defineAsyncComponent(() => import('~/components/Downtime.vue'));

const { user, accountType: userAccountType } = useUserDerivedDetails();

const currentInstance = getCurrentInstance();
const appInstance = currentInstance?.appContext.app;
const featureFlags = appInstance?.config.globalProperties.$featureFlags;

const DEFAULT_LAYOUT = 'default';

const route = useRoute();

const layoutName = ref(route.meta?.layout as string ?? DEFAULT_LAYOUT);

const layout = computed(() => {
  if (layoutName.value in Layouts) {
    return Layouts[layoutName.value];
  }
  return Layouts.default;
});

const isDowntimeEnabled = computed(() => featureFlags?.isEnabled('quizizz-downtime'));

if (!isServer()) {
  watchForLibraryLimits();
  watch(
    route,
    (to) => {
      if (to.meta.layout !== layoutName.value) {
        if (to.meta?.layout) {
          layoutName.value = to.meta.layout as string;
        } else {
          layoutName.value = DEFAULT_LAYOUT;
        }
      }
    },
    { immediate: true },
  );
}

onMounted(() => {
  trackNewSnDUser();
});

async function trackNewSnDUser() {
  /**
   * New SnD rostered Visited and Not Activated cohort: New rostered users, who were not users of Quizizz before they were on a SnD plan,
   * and have visited Quizizz but not activated(Hosted a game)
   *
   * New SnD rostered Activated cohort: New rostered users, who were not users of Quizizz before they were on a SnD plan, and have activated
   */
  const subscriptionStartDate = user.value.subscription?.startAt ? new Date(user.value.subscription?.startAt).getTime() : 0;

  const userCreationDate = user.value.createdAt ? new Date(user.value.createdAt).getTime() : 0;
  if (userAccountType.value !== Constants.AccountTypes.SCHOOL_AND_DISTRICT || !(subscriptionStartDate && userCreationDate)) {
    return;
  }

  if (userCreationDate >= subscriptionStartDate) {
    const QuizService = (await import('~/services/apis/QuizService')).default;
    const gamesResponse = await QuizService.getGames();
    const gamesCreated = get(gamesResponse, 'data.totalGames', '');
    const hotjarEventName = gamesCreated === 0
      ? HotjarEvents.NEW_SND_NON_ACTIVATED_USER_VISITED
      : HotjarEvents.NEW_SND_ACTIVATED_USER_VISITED;
    HotjarService.triggerEvent(hotjarEventName);
  }
}
</script>
